import { useMemo } from "react";
import { observer } from "mobx-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { editor } from "@paths";
import { useStores } from "@hooks";
import { useLibraryStore } from "../providers";
import {
  EstimateEditorSettingsProvider,
  useEstimateStructure,
  useNavigationKeybinds,
  BreakdownSettings,
  EditorContainer,
  LibrarySection,
} from "@tools";
import { Button } from "@components";
import { Grid } from "@material-ui/core";
import { ArrowBack } from "@assets";
import { Notifications, NotificationsNone } from "@material-ui/icons";
import classnames from "classnames";
import useStyle from "@tools/EstimateEditor/styles/EditorContent.style";
import useTableStyle from "@tools/EstimateEditor/styles/CommonTableStyles.style";

export const LibraryContainer = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { userStore } = useStores();
  const libraryStore = useLibraryStore();
  const libraryStructure = useEstimateStructure();
  const classes = useStyle();
  const tableClasses = useTableStyle();
  
  const { pending, hasSuggestions } = libraryStore;
  const { sections } = libraryStructure;
  const { isManager } = userStore;
  
  useNavigationKeybinds(libraryStructure);
  // const columnWidthClasses = useColumnWidthClasses(false, visibleBreakdownColumns)();

  const allowEdition = useMemo(
    () => isManager && !pending,
    [ isManager, pending ]
  );

  const handleHistoryBack = () => navigate(-1);

  // const handleShowPrices = () => (
  //   libraryStructure.setBreakdownColumnVisibility(BreakdownColumns.PRICE, !libraryStructure.settings.showBreakdownPriceColumn)
  // )

  const showSuggestedElements = () => {
    libraryStore.setPendingState(!pending);
  };

  const previouslyOpenedProject = useMemo(
    () => state?.from.match(editor(".*")),
    []
  );
  
  return (
    <EstimateEditorSettingsProvider
      hasEditorPrivileges={allowEdition}
      hasSellerPrivileges={allowEdition}
      allowInteractions={allowEdition}
      displayLibraryElementStatus={pending}
      allowLibraryElementStatusChange={isManager}
      noTurningOff
      isLibrary
    >
      <EditorContainer isLibrary>
        <Grid
          item container
          direction="column"
          alignItems="center"
          className={classnames("w-full mt-6", classes.scrollableContainer)}
        >
          <Grid item container className={tableClasses.root}>
            <Grid
              item
              container
              justifyContent="space-between"
              alignItems="center"
              wrap="nowrap"
              name="table_title"
              className="mb-9"
            >
              <Grid item container className="w-max">
                <h3 className="preset-title text-xl">{t("views.library.title")}</h3>
                {previouslyOpenedProject && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleHistoryBack}
                    className="ml-4 cursor-pointer"
                  >
                    {t("views.library.back")}
                  </Button>
                )}
                {isManager && (
                  <Button
                    variant="text"
                    size="small"
                    onClick={showSuggestedElements}
                    className="ml-4"
                    icon={
                      !pending ? (
                        hasSuggestions ? (
                          <Notifications />
                        ) : (
                          <NotificationsNone />
                        )
                      ) : (
                        <ArrowBack />
                      )
                    }
                  >
                    {t(`views.library.suggestions${pending ? "_back" : ""}`)}
                  </Button>
                )}
              </Grid>
              {
                isManager &&
                <Grid item container className="w-max" alignItems="center">
                  <BreakdownSettings noViewLevel noRounding noDescriptionMode />
                  {/*<p className="mx-2">{t("views.editor.show_prices")}</p>*/}
                  {/*<Switch*/}
                  {/*  color="primary"*/}
                  {/*  checked={settings.showBreakdownPriceColumn}*/}
                  {/*  onChange={handleShowPrices}*/}
                  {/*  name={*/}
                  {/*    settings.showBreakdownPriceColumn*/}
                  {/*      ? "Hide prices from breakdown"*/}
                  {/*      : "Show prices in breakdown"*/}
                  {/*  }*/}
                  {/*/>*/}
                </Grid>
              }
            </Grid>
            <Grid
              item container
              wrap="nowrap"
              direction="column"
            >
              {sections.map((section, index) => (
                <LibrarySection
                  key={section.id}
                  section={section}
                  index={index}
                />
              ))}
              {!sections?.length && (
                <p>
                  {t("views.library.pending_empty")}
                </p>
              )}
            </Grid>
          </Grid>
        </Grid>
      </EditorContainer>
    </EstimateEditorSettingsProvider>
  );
});
